import { z } from 'zod';
import { ProgramParticipantStatus } from './programs';

// Advisory committees and their availability polls (/api/committees). Schemas check shape only:
// eligibility, the under-18 rules, youthDataApproved (super admin) and every lookup stay in
// committeeService and availabilityService. The lists below mirror config/committees.ts and the
// models (the tests compare them). A person's age is held as a band, never a date of birth.

const RowId = z.string().regex(/^[0-9a-f-]{36}$/i, 'Not an id');

export const CommitteeRole = z.enum(['chair', 'co_chair', 'member', 'observer', 'staff']);
export type CommitteeRole = z.infer<typeof CommitteeRole>;

export const CommitteeAgeBand = z.enum(['under_18', '18_29', 'adult']);
export type CommitteeAgeBand = z.infer<typeof CommitteeAgeBand>;

export const CommitteePerspective = z.enum([
  'youth',
  'caregiver',
  'professional_graduate',
  'professional',
  'trainee',
  'researcher',
  'industry',
  'ngo',
  'pi',
  'staff',
  'other',
]);
export type CommitteePerspective = z.infer<typeof CommitteePerspective>;

export const CommitteeCompensationKind = z.enum(['honorarium', 'gift_card', 'none']);
export type CommitteeCompensationKind = z.infer<typeof CommitteeCompensationKind>;

export const CommitteeCycleState = z.enum(['planning', 'open', 'active', 'closed']);
export type CommitteeCycleState = z.infer<typeof CommitteeCycleState>;

export const CommitteeMeetingMode = z.enum(['online', 'in_person', 'hybrid']);
export type CommitteeMeetingMode = z.infer<typeof CommitteeMeetingMode>;

export const CommitteeMessageKind = z.enum(['invitation', 'confirmation', 'reminder']);
export type CommitteeMessageKind = z.infer<typeof CommitteeMessageKind>;

const Text = z.string().nullish();
const Ids = z.array(z.string());
const Year = z.union([z.number().int(), z.string().regex(/^\d{4}$/, 'is not a year')]);

// ---------------------------------------------------------------- params and queries

/** The committee by id or key. */
export const CommitteeProgramParams = z.strictObject({ program: z.string().min(1) });
export const CommitteeCycleParams = z.strictObject({ cycleId: RowId });
/** A participant, meeting (event) or poll id. */
export const CommitteeIdParams = z.strictObject({ id: RowId });

export const CommitteeRolesQuery = z.strictObject({ userId: z.string().optional(), contactId: z.string().optional() });
export type CommitteeRolesQuery = z.input<typeof CommitteeRolesQuery>;

/** Comma-separated participant ids; ids that are not in the cycle are ignored by the service. */
export const CommitteeSelectionQuery = z.strictObject({
  participantIds: z.union([z.string(), z.array(z.string())]).optional(),
});
export type CommitteeSelectionQuery = z.input<typeof CommitteeSelectionQuery>;

// ---------------------------------------------------------------- requests

export const CommitteeSyncRequest = z.strictObject({ year: Year.nullish(), dryRun: z.boolean().optional() });
export type CommitteeSyncRequest = z.input<typeof CommitteeSyncRequest>;

export const CommitteeCreateCycleRequest = z.strictObject({ year: Year, state: CommitteeCycleState.optional() });
export type CommitteeCreateCycleRequest = z.input<typeof CommitteeCreateCycleRequest>;

export const CommitteeUpdateCycleRequest = z.strictObject({ state: CommitteeCycleState.optional() });
export type CommitteeUpdateCycleRequest = z.input<typeof CommitteeUpdateCycleRequest>;

const memberFields = {
  perspective: z.union([CommitteePerspective, z.literal('')]).nullish(),
  region: Text,
  language: Text,
  ageBand: z.union([CommitteeAgeBand, z.literal('')]).nullish(),
};

/** A member (userId or email), a contact (contactId) or a new external contact. */
export const CommitteeAddMemberRequest = z.strictObject({
  ...memberFields,
  role: z.union([CommitteeRole, z.literal('')]).optional(),
  status: z.union([ProgramParticipantStatus, z.literal('')]).optional(),
  note: Text,
  userId: Text,
  contactId: Text,
  email: Text,
  newContact: z.strictObject({ name: Text, email: Text, organization: Text }).nullish(),
});
export type CommitteeAddMemberRequest = z.input<typeof CommitteeAddMemberRequest>;

export const CommitteeUpdateMemberRequest = z.strictObject({
  ...memberFields,
  role: z.union([CommitteeRole, z.literal('')]).optional(),
});
export type CommitteeUpdateMemberRequest = z.input<typeof CommitteeUpdateMemberRequest>;

export const CommitteeStatusRequest = z.strictObject({ status: ProgramParticipantStatus, note: Text });
export type CommitteeStatusRequest = z.input<typeof CommitteeStatusRequest>;

export const CommitteeSelectionRequest = z.strictObject({ participantIds: Ids, note: Text });
export type CommitteeSelectionRequest = z.input<typeof CommitteeSelectionRequest>;

/** POST creates (title and startsAt required by the service), PATCH changes the fields sent. */
export const CommitteeMeetingRequest = z.strictObject({
  title: Text,
  startsAt: Text,
  endsAt: Text,
  mode: z.union([CommitteeMeetingMode, z.literal('')]).optional(),
  location: Text,
  briefUrl: Text,
  minutesUrl: Text,
  minutesLabel: Text,
});
export type CommitteeMeetingRequest = z.input<typeof CommitteeMeetingRequest>;

/** Preview and send take the same body. */
export const CommitteeMessageRequest = z.strictObject({
  kind: CommitteeMessageKind,
  participantIds: Ids,
  meetingId: Text,
  vars: z.strictObject({ responseBy: Text, responseByFr: Text, joinInfo: Text, joinInfoFr: Text }).optional(),
});
export type CommitteeMessageRequest = z.input<typeof CommitteeMessageRequest>;

export const CommitteeAttendanceRequest = z.strictObject({
  entries: z.array(
    z.strictObject({
      participantId: z.string(),
      attended: z.boolean().optional(),
      minutes: z.number().int().nullish(),
    }),
  ),
});
export type CommitteeAttendanceRequest = z.input<typeof CommitteeAttendanceRequest>;

/** Zoom attendee report (CSV text); a dry run unless confirm is true. */
export const CommitteeZoomUploadRequest = z.strictObject({
  fileName: Text,
  content: Text,
  confirm: z.boolean().optional(),
});
export type CommitteeZoomUploadRequest = z.input<typeof CommitteeZoomUploadRequest>;

/** youthDataApproved is refused by the service unless the operator is a super admin. */
export const CommitteeSettingsRequest = z.strictObject({
  compensation: z
    .strictObject({
      kind: CommitteeCompensationKind,
      amountCents: z.union([z.number(), z.string()]).nullish(),
    })
    .nullish(),
  externalMembers: z.boolean().optional(),
  nameFr: Text,
  eligibility: z
    .strictObject({
      residentOfCanada: z.boolean().optional(),
      canadianInstitution: z.boolean().optional(),
      perspectives: z.array(CommitteePerspective).optional(),
      ageBands: z.array(CommitteeAgeBand).optional(),
      perspectiveAgeBands: z.partialRecord(CommitteePerspective, z.array(CommitteeAgeBand)).optional(),
      disciplines: z.array(z.string()).optional(),
    })
    .nullish(),
  youthDataApproved: z.boolean().optional(),
});
export type CommitteeSettingsRequest = z.input<typeof CommitteeSettingsRequest>;

export const CommitteePollRequest = z.strictObject({
  title: Text,
  slots: z.array(z.strictObject({ startsAt: z.string(), endsAt: Text })),
});
export type CommitteePollRequest = z.input<typeof CommitteePollRequest>;

export const CommitteePollLinksRequest = z.strictObject({ participantIds: Ids });
export type CommitteePollLinksRequest = z.input<typeof CommitteePollLinksRequest>;

export const CommitteePollCloseRequest = z.strictObject({
  chosenSlotId: Text,
  createMeeting: z.boolean().optional(),
  title: Text,
});
export type CommitteePollCloseRequest = z.input<typeof CommitteePollCloseRequest>;
